import { actionTypes, allowedExchangeMethods, orderTransactionTypes } from '@/constants'

const getItemsExcludingBundleChildren = (items = [], filterFn) => {
  const bundleChildIds = items.flatMap(item => (item.bundleItems || []).map(child => child.id))

  return items.filter(item => filterFn(item) && !bundleChildIds.includes(item.id))
}

export const getSumOfAmounts = amounts => {
  return Object.values(amounts).reduce((acc, val) => acc + Number(val), 0)
}

export const getVariantsText = options => {
  if (options) {
    return Object.keys(options)
      .sort()
      .map(key => options[key])
      .join(' · ')
  }
}

const getSelectedVariantPrice = item => {
  const selectedVariant = item?.variants?.find(variant => variant.id === item?.variantId)

  return Number(selectedVariant?.price || item?.price)
}

export const getNewItem = item => {
  if (item.exchangeType === allowedExchangeMethods.otherProductsVariants && item?.selectedProduct) {
    return {
      label: item?.selectedProduct?.name || item?.product?.name,
      amount: Number(item?.selectedProduct?.price || item?.selectedProduct?.from_price || item?.price),
      variant: getVariantsText(item?.selectedProduct?.options),
    }
  }

  if (item.exchangeType === allowedExchangeMethods.variants) {
    return {
      label: `${item?.product?.name}`,
      amount: getSelectedVariantPrice(item),
      variant: getVariantsText(item.options),
    }
  }
  return null
}

const getExtraProductsAmount = (items = []) => {
  return getItemsExcludingBundleChildren(items, item => item.exchangeType && item.extraProducts?.length > 0)
    .flatMap(item => item.extraProducts)
    .reduce((acc, extraProduct) => {
      return Number(acc) + Number(extraProduct?.price || 0)
    }, 0)
}

const getNewProductsAmountFromExchange = (items = []) => {
  return getItemsExcludingBundleChildren(
    items,
    item => item.exchangeType && item.actionType === actionTypes.exchange
  ).reduce((acc, item) => {
    if (item.exchangeType === allowedExchangeMethods.otherProductsVariants) {
      return Number(acc) + Number(item?.selectedProduct?.price || item?.selectedProduct?.from_price || 0)
    } else {
      return Number(acc) + getSelectedVariantPrice(item)
    }
  }, 0)
}

export const getNewProductsAmount = (items = []) => {
  return getNewProductsAmountFromExchange(items) + getExtraProductsAmount(items)
}

export const getSelectedProductsAmount = (items = []) => {
  return items?.reduce((acc, item) => {
    return Number(acc) + Number(item.price)
  }, 0)
}

const getNewProductItemsFromExchange = (items = []) => {
  return getItemsExcludingBundleChildren(items, item => item.exchangeType && item.actionType === actionTypes.exchange)
    .map(item => getNewItem(item))
    .filter(newItem => newItem)
}

const getExtraProducts = (items = []) => {
  return getItemsExcludingBundleChildren(items, item => item.exchangeType && item.extraProducts?.length > 0)
    .flatMap(item => item.extraProducts)
    ?.map(extraProduct => ({
      label: extraProduct?.product?.name,
      amount: Number(extraProduct?.price),
      variant: extraProduct?.options && Object.values(extraProduct?.options).join(' · '),
    }))
}

export const getNewProductsItems = (items = []) => {
  const newItems = getNewProductItemsFromExchange(items)
  const extraItems = getExtraProducts(items)

  return [...newItems, ...extraItems]
}

export const getSummaryProductItems = (items = []) => {
  return items?.map(item => ({
    label: item?.product?.name,
    amount: item?.price,
    variant: getVariantsText(item?.variant?.options),
  }))
}

export const getCarbonOffsetAmount = (carbonOffset, deliveryMethod) => {
  if (carbonOffset) {
    return Number(deliveryMethod?.carbon_offset_fee)
  }
  return 0
}

const getDeliveryMethodFees = orderInfo => {
  if (!orderInfo?.deliveryMethod) return {}

  return {
    delivery_method_fee_amount: Number(orderInfo.deliveryMethod.total_fee),
    carbon_offset_fee_amount: getCarbonOffsetAmount(orderInfo.carbonOffset, orderInfo.deliveryMethod),
  }
}

export const areAmountMapsEqual = (currentMap, nextMap) => {
  const currentKeys = Object.keys(currentMap)
  const nextKeys = Object.keys(nextMap)

  return currentKeys.length === nextKeys.length && nextKeys.every(key => currentMap[key] === nextMap[key])
}

const isExchangeFlow = orderInfo => {
  return orderInfo?.type === orderTransactionTypes.exchange || orderInfo?.type === orderTransactionTypes.mixed
}

export const getFees = ({ isProductSelectionPage, orderAmount, deliveryMethodFees, inputFees, previewFees }) => {
  if (isProductSelectionPage) {
    return { ...previewFees, ...deliveryMethodFees, ...inputFees }
  }

  return { ...orderAmount?.fees, ...deliveryMethodFees, ...inputFees }
}

export const getBonuses = ({ isProductSelectionPage, orderAmount, inputBonuses, previewBonuses }) => {
  if (isProductSelectionPage) {
    return { ...previewBonuses, ...inputBonuses }
  }

  return { ...orderAmount?.bonuses, ...inputBonuses }
}

const getFeeMessageKey = ({ message }) => JSON.stringify(message)

const getUniqueFeeMessages = (feeMessages = []) => {
  const feeMessageKeys = feeMessages.map(getFeeMessageKey)

  return feeMessages.filter((feeMessage, index) => feeMessageKeys.indexOf(feeMessageKeys[index]) === index)
}

export const getFeeMessages = ({ isProductSelectionPage, persistedFeeMessages, previewFeeMessages }) => {
  if (isProductSelectionPage) {
    return getUniqueFeeMessages(previewFeeMessages)
  }

  return getUniqueFeeMessages(persistedFeeMessages)
}

export const getRequestedProducts = (orderInfo, items) => {
  if (!isExchangeFlow(orderInfo)) {
    return { total: 0, products: [] }
  }

  return { total: getNewProductsAmount(items), products: getNewProductsItems(items) }
}

const getProductsToReturnTotal = ({ isProductSelectionPage, orderAmount, clientTotal }) => {
  if (isProductSelectionPage) return clientTotal

  return orderAmount?.original_amount ?? clientTotal
}

const getNewRequestedProductsTotal = ({ isProductSelectionPage, orderAmount, clientTotal }) => {
  if (isProductSelectionPage) return clientTotal

  return orderAmount?.exchange_items_amount ?? clientTotal
}

export const getRemainingAmount = ({
  isProductSelectionPage,
  hasItems,
  orderAmount,
  productsToReturnTotal,
  newRequestedProductsTotal,
  feesTotal,
  bonusesTotal,
}) => {
  const shouldUseBackendRemaining = !isProductSelectionPage && !hasItems && orderAmount?.remaining_amount != null

  if (shouldUseBackendRemaining) return Number(orderAmount.remaining_amount)

  return Number(productsToReturnTotal) - Number(newRequestedProductsTotal) - feesTotal + bonusesTotal
}

export const computeSummary = ({ items, orderInfo, orderAmount, summaryInputs, isProductSelectionPage }) => {
  const safeItems = items || []

  const fees = getFees({
    isProductSelectionPage,
    orderAmount,
    deliveryMethodFees: getDeliveryMethodFees(orderInfo),
    inputFees: summaryInputs?.fees || {},
    previewFees: summaryInputs?.previewFees || {},
  })
  const bonuses = getBonuses({
    isProductSelectionPage,
    orderAmount,
    inputBonuses: summaryInputs?.bonuses || {},
    previewBonuses: summaryInputs?.previewBonuses || {},
  })
  const feesTotal = getSumOfAmounts(fees)
  const bonusesTotal = getSumOfAmounts(bonuses)

  const requestedProducts = getRequestedProducts(orderInfo, safeItems)
  const productsToReturnTotal = getProductsToReturnTotal({
    isProductSelectionPage,
    orderAmount,
    clientTotal: getSelectedProductsAmount(safeItems),
  })
  const newRequestedProductsTotal = getNewRequestedProductsTotal({
    isProductSelectionPage,
    orderAmount,
    clientTotal: requestedProducts.total,
  })

  const remainingAmount = getRemainingAmount({
    isProductSelectionPage,
    hasItems: safeItems.length > 0,
    orderAmount,
    productsToReturnTotal,
    newRequestedProductsTotal,
    feesTotal,
    bonusesTotal,
  })

  return {
    productsToReturn: {
      total: productsToReturnTotal,
      products: getSummaryProductItems(safeItems),
    },
    newRequestedProducts: {
      total: newRequestedProductsTotal,
      products: requestedProducts.products,
    },
    fees: {
      total: feesTotal,
      fees,
    },
    bonuses: {
      total: bonusesTotal,
      bonuses,
    },
    remaining_amount: remainingAmount,
    currency: orderInfo?.currency || orderAmount?.currency,
  }
}

export const getContextWithResetSummaryInputs = (context, reset) => {
  const summaryInputs = context?.summaryInputs || {}
  const hasFees = Object.keys(summaryInputs.fees || {}).length > 0
  const hasBonuses = Object.keys(summaryInputs.bonuses || {}).length > 0
  const shouldResetDeliveryMethod = Boolean(reset?.resetDeliveryMethod && context?.orderInfo?.deliveryMethod)

  if (context?.summaryInputs && !hasFees && !hasBonuses && !shouldResetDeliveryMethod) {
    return context
  }

  return {
    ...context,
    orderInfo: shouldResetDeliveryMethod ? { ...context?.orderInfo, deliveryMethod: null } : context?.orderInfo,
    summaryInputs: {
      fees: {},
      bonuses: {},
      previewFees: summaryInputs.previewFees || {},
      previewBonuses: summaryInputs.previewBonuses || {},
      previewFeeMessages: summaryInputs.previewFeeMessages || [],
    },
  }
}
